import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { DocumentArrowDownIcon } from '@heroicons/react/24/outline';
import {
  selectCurrentView,
  selectEstablishments,
  selectTotal,
  ProspectionViewValues
} from '../../store/slices/nouveauxAdherentsSlice';
import ExportModal from './ExportModal';
import api from '../../api';
import { showSuccessToast, showErrorToast } from '../../utils/toastUtils';

interface ExportButtonProps {
  formData: any; // Current filter form data
}

const ExportButton: React.FC<ExportButtonProps> = ({ formData }) => {
  const currentView = useSelector(selectCurrentView);
  const establishments = useSelector(selectEstablishments);
  const totalResults = useSelector(selectTotal);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const successToastId = 'export-success';
  const errorToastId = 'export-error';

  // Get export configuration based on current view
  const getExportConfig = () => {
    switch (currentView) {
      case ProspectionViewValues.NEW_ESTABLISHMENTS_NEW_COMPANIES:
        return {
          endpoint: '/etablissements/export/nouvelles-entreprises',
          type: 'Nouvelles entreprises',
          description: 'Établissements sièges non clients dans votre secteur'
        };
      case ProspectionViewValues.NEW_ESTABLISHMENTS_EXISTING_COMPANIES_IN_YOUR_SECTOR:
        return {
          endpoint: '/etablissements/export/entreprises-locales',
          type: 'Entreprises locales',
          description: 'Établissements non sièges non clients dont le siège est dans votre secteur'
        };
      case ProspectionViewValues.NEW_ESTABLISHMENTS_EXISTING_COMPANIES_OUT_OF_SECTOR:
        return {
          endpoint: '/etablissements/export/entreprises-exterieures',
          type: 'Entreprises extérieures',
          description: 'Établissements non sièges non clients dont le siège est hors de votre secteur'
        };
      case ProspectionViewValues.NON_CLIENT_FROM_CLIENT_COMPANIES:
        return {
          endpoint: '/etablissements/export/groupes-clients',
          type: 'Groupes clients',
          description: 'Entreprises non-clientes dans des groupes clients'
        };
      default:
        return null;
    }
  };

  const handleExportClick = () => {
    setIsModalOpen(true);
  };

  const handleConfirmExport = async () => {
    setIsModalOpen(false);
    setIsExporting(true);

    const config = getExportConfig();
    if (!config) {
      showErrorToast('Configuration d\'export non trouvée', errorToastId);
      setIsExporting(false);
      return;
    }

    try {
      // Prepare query parameters based on form data
      const params = new URLSearchParams();
      
      if (formData.dateFrom) params.append('fromDate', formData.dateFrom);
      if (formData.dateTo) params.append('dateTo', formData.dateTo);
      if (formData.departement) params.append('departement', formData.departement);
      if (formData.nafCodes?.length > 0) {
        formData.nafCodes.forEach((code: string) => params.append('nafCodes', code));
      }
      if (formData.postalCodes?.length > 0) {
        formData.postalCodes.forEach((code: string) => params.append('postalCodes', code));
      }
      if (formData.excludeMicroEntreprises) params.append('excludeMicroEntreprises', 'true');
      if (formData.excludedProspectListIds?.length > 0) {
        formData.excludedProspectListIds.forEach((id: number) => params.append('excludedProspectListIds', String(id)));
      }
      if (formData.excludeProspectsMode) {
        params.append('excludeProspectsMode', formData.excludeProspectsMode);
      }

      // Make the request with response type blob for file download
      const response = await api.get(`${config.endpoint}?${params.toString()}`, {
        responseType: 'blob',
      });

      // Get filename from headers or use default
      const contentDisposition = response.headers['content-disposition'] || response.headers['Content-Disposition'];
      let filename = `${config.type.toLowerCase().replace(/\s+/g, '-')}-${new Date().toISOString().split('T')[0]}.xlsx`;
      
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?([^"]*)"?/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      // Create blob URL and trigger download
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      // Use the total from Redux state since headers might not be accessible due to CORS
      const countText = totalResults > 0 ? totalResults.toLocaleString() : 'plusieurs';
      const wasTruncated = totalResults > 1000;
      
      if (wasTruncated) {
        showSuccessToast(`Export terminé (${countText} résultats, limité à 1000)`, successToastId);
      } else {
        showSuccessToast(`Export terminé (${countText} résultats)`, successToastId);
      }

    } catch (error: any) {
      console.error('Export error:', error);
      if (error.response?.data) {
        showErrorToast(`Erreur lors de l'export: ${error.response.data.error || error.message}`, errorToastId);
      } else {
        showErrorToast('Erreur lors de l\'export des données', errorToastId);
      }
    } finally {
      setIsExporting(false);
    }
  };

  const handleCancelExport = () => {
    setIsModalOpen(false);
  };

  const config = getExportConfig();
  if (!config) return null;

  return (
    <>
      <button
        onClick={handleExportClick}
        disabled={isExporting || !establishments || establishments.length === 0}
        className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <DocumentArrowDownIcon className="h-4 w-4 mr-2" />
        {isExporting ? 'Export en cours...' : 'Exporter Excel'}
      </button>

      <ExportModal
        isOpen={isModalOpen}
        totalResults={totalResults}
        exportType={config.type}
        onConfirm={handleConfirmExport}
        onCancel={handleCancelExport}
      />
    </>
  );
};

export default ExportButton; 